import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, assetUrl } from "@/lib/api";
import { useLanguage, useT } from "@/context/LanguageContext";
import EmailLink from "@/components/common/EmailLink";

const HERO_BG = "https://customer-assets.emergentagent.com/job_cms-control-4/artifacts/u975ewiv_ChatGPT%20Image%2022%20de%20abr.%20de%202026%2C%2017_41_13.png";
const FOUNDER_PORTRAIT = "https://images.pexels.com/photos/5764212/pexels-photo-5764212.png";

function renderContactTitle(title) {
  const safe = title || "Begin a conversation.";
  const words = safe.split(" ");
  if (words.length <= 1) return <span className="italic text-gold-light">{safe}</span>;
  const lead = words.slice(0, -1).join(" ");
  const last = words.slice(-1)[0];
  return (
    <Fragment>
      {lead} <span className="italic text-gold-light">{last}</span>
    </Fragment>
  );
}

export default function Home() {
  const { lang } = useLanguage();
  const t = useT();
  const [bundle, setBundle] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/public/bundle", { params: { lang } })
      .then(({ data }) => setBundle(data))
      .catch(() => {});
  }, [lang]);

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!bundle) {
    return <div className="min-h-screen flex items-center justify-center font-display text-gold-light tracking-widest">{t("loading")}</div>;
  }

  const { settings, about, homepage, services, projects } = bundle;
  const featuredProjects = projects.filter((p) => p.featured).length ? projects.filter((p) => p.featured) : projects.slice(0, 4);
  const [heroLine1, heroLine2] = (homepage?.hero_headline || "Private Digital Systems. Built for Performance.").split(".").map((s) => s.trim()).filter(Boolean);
  const sectionOrder = (key) => homepage?.sections?.[key]?.order ?? 99;

  return (
    <>
      {/* ==================== HERO ==================== */}
      <section id="home" className="relative min-h-[96vh] overflow-hidden bg-paper">
        {/* Background artwork */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-85"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        {/* Subtle white wash to push text contrast */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.75) 40%, rgba(255,255,255,0.35) 70%, rgba(255,255,255,0.15) 100%)",
          }}
        />
        {/* Top gradient wash under header */}
        <div className="absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-white/90 to-transparent pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-40 pb-24 md:pt-48 md:pb-32 grid md:grid-cols-12 gap-12 items-center min-h-[96vh]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="md:col-span-7 lg:col-span-6"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px w-12 bg-[var(--gold)]" />
              <div className="eyebrow-light" data-testid="hero-eyebrow">{t("hero.eyebrow")}</div>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-ink leading-[1.02] tracking-tight">
              {heroLine1 || "Private Digital Systems"}.
              <br />
              <span className="italic font-light text-graphite">
                {heroLine2 || "Built for Performance"}
                <span className="text-gold-light">.</span>
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-graphite leading-relaxed">
              {homepage?.hero_subheadline || "Exclusive digital architecture engineered for operators who refuse templates and demand precision."}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/portal/register" className="btn-gold-solid-light" data-testid="hero-cta-primary">
                {homepage?.hero_cta_text || "Start a Project"} <ArrowRight size={14} />
              </Link>
              <Link to="/portfolio" className="btn-gold-light" data-testid="hero-cta-secondary">
                {t("hero.exploreCta")} <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.div>

          <HeroVideo
            enabled={homepage?.hero_video_enabled}
            desktopUrl={homepage?.hero_video_url}
            mobileUrl={homepage?.hero_video_mobile_url}
            poster={homepage?.hero_video_poster}
            layout={homepage?.hero_video_layout}
          />
        </div>

        {/* Mobile-only stacked video (only renders when enabled) */}
        {homepage?.hero_video_enabled && (homepage?.hero_video_mobile_url || homepage?.hero_video_url) && (
          <div className="md:hidden relative max-w-[1400px] mx-auto px-6 -mt-12 mb-12">
            <HeroVideo
              enabled
              desktopUrl={homepage?.hero_video_url}
              mobileUrl={homepage?.hero_video_mobile_url}
              poster={homepage?.hero_video_poster}
              layout={homepage?.hero_video_layout}
              variant="mobile"
            />
          </div>
        )}

        {/* Pillars strip */}
        <div className="relative border-t border-line bg-paper/70 backdrop-blur-sm">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {["Digital Ecosystems", "Automation Architecture", "Intelligent Systems", "Private Solutions"].map((p, i) => (
              <div key={p} className="flex items-center gap-3">
                <span className="text-[0.6rem] text-gold-light font-medium tracking-widest">0{i + 1}</span>
                <span className="eyebrow-light text-[0.68rem] text-ink">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ORDERED SECTIONS (reorder-aware) ==================== */}
      <div className="flex flex-col">

      {/* ==================== SERVICES ==================== */}
      {homepage?.sections?.services?.enabled !== false && (
        <section id="services" className="relative py-24 md:py-36 bg-ivory" style={{ order: sectionOrder("services") }}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-12 gap-8 items-end mb-20">
              <div className="md:col-span-6">
                <div className="eyebrow-light mb-4">{t("services.eyebrow")}</div>
                <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] text-ink">
                  {t("services.title.lead")} <span className="italic text-gold-light">{t("services.title.italic")}</span>.
                  <br />{t("services.title.tail")}
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 text-graphite text-base md:text-lg leading-relaxed">
                {t("services.intro")}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((s, i) => {
                const Icon = Icons[s.icon] || Icons.Sparkles;
                return (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, delay: i * 0.08 }}
                    className="card-paper p-10 md:p-12 group relative"
                    data-testid={`service-card-${i}`}
                  >
                    <div className="flex items-start justify-between mb-10">
                      <div className="w-14 h-14 flex items-center justify-center border border-line group-hover:border-gold-light rounded-full transition-colors">
                        <Icon className="text-gold-light" size={22} strokeWidth={1.3} />
                      </div>
                      <span className="eyebrow-light text-[0.6rem]">{String(i + 1).padStart(2, "0")} / 04</span>
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-4 text-ink leading-tight">
                      {s.title}
                    </h3>
                    <p className="text-graphite leading-relaxed max-w-md">{s.short_description}</p>
                    <div className="mt-8 rule-gold-left w-16 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ==================== METHOD / PHILOSOPHY ====================
          Renders right after Services. CSS `order` must be an integer; we use
          the same value as Services so flex falls back to source-order, which
          places Method directly after Services and before Portfolio. */}
      <section id="method" className="relative py-24 md:py-36 bg-paper" style={{ order: sectionOrder("services") }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className="md:col-span-5"
            >
              <div className="eyebrow-light mb-5" data-testid="method-eyebrow">
                {settings?.method_eyebrow || "Our Approach"}
              </div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] text-ink" data-testid="method-title">
                {settings?.method_title || "The Gomes Method"}
                <span className="text-gold-light">.</span>
              </h2>
              <div className="rule-gold-left w-20 mt-10 hidden md:block" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="md:col-span-6 md:col-start-7 space-y-6 text-graphite text-base md:text-lg leading-[1.8]"
              data-testid="method-body"
            >
              {settings?.method_paragraph_1 && <p>{settings.method_paragraph_1}</p>}
              {settings?.method_paragraph_2 && <p>{settings.method_paragraph_2}</p>}
              {settings?.method_paragraph_3 && (
                <p className="text-ink font-display italic text-xl md:text-2xl leading-[1.5] pt-4">
                  {settings.method_paragraph_3}
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== PORTFOLIO ==================== */}
      {homepage?.sections?.portfolio?.enabled !== false && (
        <section id="portfolio" className="relative py-24 md:py-36 bg-paper" style={{ order: sectionOrder("portfolio") }}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex flex-wrap items-end justify-between gap-8 mb-16">
              <div>
                <div className="eyebrow-light mb-4">{t("portfolio.eyebrow")}</div>
                <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] text-ink">
                  {t("portfolio.title.lead")} <span className="italic text-gold-light">{t("portfolio.title.italic")}</span>{t("portfolio.title.tail")}
                </h2>
              </div>
              <Link to="/portfolio" className="btn-ink" data-testid="portfolio-view-all">
                {t("portfolio.viewAll")} <ArrowUpRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {featuredProjects.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className={`${i === 0 ? "md:col-span-8 md:row-span-2" : "md:col-span-4"}`}
                  data-testid={`featured-project-${i}`}
                >
                  <Link
                    to={`/portfolio/${p.slug}`}
                    className="group block card-paper overflow-hidden h-full"
                  >
                    <div className={`${i === 0 ? "aspect-[4/5] md:aspect-[16/11]" : "aspect-[4/3]"} overflow-hidden bg-ivory`}>
                      {p.cover_image_url ? (
                        <img
                          src={assetUrl(p.cover_image_url)}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms]"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-display text-gold-light/40 text-5xl">
                          {p.title.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="px-7 py-6 border-t border-line flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="eyebrow-light text-[0.6rem] mb-2">{p.category || "Commission"} {p.year && `· ${p.year}`}</div>
                        <h3 className="font-display text-2xl md:text-3xl tracking-tight text-ink group-hover:text-gold-light transition-colors">
                          {p.title}
                        </h3>
                        <p className="text-graphite text-sm mt-2 line-clamp-2 max-w-md">{p.short_description}</p>
                      </div>
                      <ArrowUpRight className="text-mute group-hover:text-gold-light transition-colors flex-shrink-0" size={18} />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== FEATURED INSIGHTS ====================
          Renders right after Portfolio (between Portfolio and About).
          Uses the same flex-order trick as Method: same order as Portfolio,
          source order places it directly after. */}
      {bundle.featured_posts?.length > 0 && (
        <section id="featured-insights" className="relative py-24 md:py-36 bg-paper" style={{ order: sectionOrder("portfolio") }}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-14 md:mb-20">
              <div>
                <div className="eyebrow-light mb-4">{t("insights.featuredEyebrow")}</div>
                <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] text-ink">
                  {t("insights.featuredTitle.lead")} <span className="italic text-gold-light">{t("insights.featuredTitle.italic")}</span>{t("insights.featuredTitle.tail")}
                </h2>
              </div>
              <Link to="/insights" className="btn-ink hidden md:inline-flex" data-testid="featured-insights-view-all">
                {t("insights.viewAll")} <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {bundle.featured_posts.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.06 }}
                >
                  <Link
                    to={`/insights/${p.slug}`}
                    className="group block card-paper overflow-hidden h-full"
                    data-testid={`featured-insight-${p.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-ivory">
                      {p.cover_image_url ? (
                        <img
                          src={assetUrl(p.cover_image_url)}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms]"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-display text-gold-light/30 text-7xl italic">{p.title.charAt(0)}</div>
                      )}
                    </div>
                    <div className="p-7 border-t border-line">
                      <div className="eyebrow-light text-[0.58rem] mb-2">{p.category || "Insight"}</div>
                      <h3 className="font-display text-xl md:text-2xl tracking-tight text-ink leading-snug group-hover:text-gold-light transition-colors">{p.title}</h3>
                      <p className="text-graphite text-sm mt-3 line-clamp-3">{p.excerpt}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="mt-12 md:hidden">
              <Link to="/insights" className="btn-ink inline-flex">
                {t("insights.viewAll")} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ==================== ABOUT ==================== */}
      {homepage?.sections?.about?.enabled !== false && (
        <section id="about" className="relative py-24 md:py-36 bg-ivory" style={{ order: sectionOrder("about") }}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="md:col-span-5"
            >
              <div className="relative aspect-[4/5] overflow-hidden border border-line bg-paper">
                <img src={assetUrl(about.profile_image_url) || FOUNDER_PORTRAIT} alt={about.founder_name} className="w-full h-full object-cover" />
              </div>
              <div className="mt-6">
                <div className="font-display text-2xl text-ink italic">{about.founder_name}</div>
                <div className="eyebrow-light text-[0.62rem] mt-1">{about.founder_title}</div>
              </div>
            </motion.div>
            <div className="md:col-span-6 md:col-start-7">
              <div className="eyebrow-light mb-5">{t("about.eyebrow")}</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight text-ink mb-8">
                {about.positioning || "Private. Precise. Performance-driven."}
              </h2>
              <p className="text-graphite leading-relaxed text-lg max-w-xl">
                {about.biography}
              </p>
              <div className="rule-gold-left w-24 my-10" />
              <a href="#contact" className="btn-gold-light" data-testid="about-cta">
                {t("about.cta")} <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ==================== CONTACT ==================== */}
      {homepage?.sections?.contact?.enabled !== false && (
        <section id="contact" className="relative py-24 md:py-36 bg-paper" style={{ order: sectionOrder("contact") }}>
          <div className="max-w-[1000px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <div className="eyebrow-light mb-5">{t("contact.eyebrow")}</div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-tight text-ink">
                {renderContactTitle(settings?.contact_section_title)}
              </h2>
              <p className="mt-6 text-graphite max-w-xl mx-auto text-base md:text-lg">
                {settings?.contact_section_subtitle || "Share a few details. Expect a thoughtful, personal reply — never a template."}
              </p>
              {settings?.contact_email && (
                <div className="mt-7 flex justify-center" data-testid="contact-direct-email">
                  <EmailLink
                    email={settings.contact_email}
                    variant="card"
                    label="Or write to us directly"
                  />
                </div>
              )}
            </div>

            <form onSubmit={submitLead} className="max-w-2xl mx-auto space-y-8" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="eyebrow-light mb-2 block">{t("contact.label.name")}</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-underline"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="eyebrow-light mb-2 block">{t("contact.label.email")}</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input-underline"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow-light mb-2 block">{t("contact.label.phone")}</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-underline"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="eyebrow-light mb-2 block">{t("contact.label.message")}</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="input-underline resize-none"
                  data-testid="contact-message"
                />
              </div>
              <div className="pt-4 flex flex-wrap justify-end gap-4">
                <Link to="/portal/register" className="btn-gold-light" data-testid="contact-start-project">
                  Open private workspace <ArrowUpRight size={13} />
                </Link>
                <button type="submit" disabled={sending} className="btn-gold-solid-light disabled:opacity-50" data-testid="contact-submit">
                  {sending ? t("contact.sending") : (settings?.contact_form_cta_text || "Send Enquiry")} <ArrowRight size={14} />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
      </div>
    </>
  );
}

// ============================================================
//  HeroVideo — luxury cinematic hero card
// ============================================================
// Renders a brand video to the right of the headline (desktop) or stacked
// below it (mobile). Acts as a premium visual accent — NOT a primary focal
// point — so it's tightly capped in size and centered in its column.
//
//   layout | desktop aspect-ratio | desktop max width
//   -------|----------------------|------------------
//   portrait  4:5  (default)      | 440px (max ~550px tall)
//   landscape 16:10                | 520px (max ~325px tall)
//   square    1:1                  | 460px
function HeroVideo({ enabled, desktopUrl, mobileUrl, poster, layout, variant }) {
  if (!enabled || (!desktopUrl && !mobileUrl)) {
    // Desktop variant keeps the empty grid slot so the column-7 headline
    // doesn't reflow when the toggle is off. Mobile variant just renders
    // nothing (caller controls visibility).
    if (variant === "mobile") return null;
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.6 }}
        className="hidden md:block md:col-span-5 lg:col-span-5 lg:col-start-8"
        aria-hidden="true"
      />
    );
  }

  // Pick mobileUrl when explicitly mobile or when only mobile is set.
  const src = variant === "mobile" ? (mobileUrl || desktopUrl) : (desktopUrl || mobileUrl);
  const posterSrc = poster ? assetUrl(poster) : "";
  const videoSrc = assetUrl(src);
  const layoutKey = layout || "portrait";
  const layoutClass = `hero-video--${layoutKey}`;

  // Cap the card with inline styles so the CSS max-width rule can't lose
  // a specificity fight with parent flexbox utilities.
  const desktopMax = layoutKey === "landscape" ? 520 : layoutKey === "square" ? 460 : 440;

  const card = (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      className={`hero-video-card ${layoutClass}`}
      style={variant === "mobile" ? undefined : { maxWidth: `${desktopMax}px` }}
      data-testid={variant === "mobile" ? "hero-video-mobile" : "hero-video"}
    >
      {/* Champagne ambient halo behind the frame */}
      <div className="hero-video-halo" aria-hidden="true" />
      {/* The frame itself */}
      <div className="hero-video-frame">
        <video
          src={videoSrc}
          poster={posterSrc || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          aria-label="Gomes Digital Lab — brand video"
        />
        {/* Light cinematic vignette so the typography on the left wins the focus */}
        <div className="hero-video-vignette" aria-hidden="true" />
        {/* Soft champagne shimmer that drifts on hover */}
        <div className="hero-video-shine" aria-hidden="true" />
      </div>
      {/* Subtle floating accent dot */}
      <div className="hero-video-accent" aria-hidden="true">
        <span className="hero-video-accent-dot" />
        <span className="text-[0.58rem] uppercase tracking-[0.32em] text-graphite">Live · Studio Atelier</span>
      </div>
    </motion.div>
  );

  if (variant === "mobile") return card;
  return (
    <div className="hidden md:flex md:col-span-5 lg:col-span-5 lg:col-start-8 items-center justify-center">
      {card}
    </div>
  );
}

